/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { borderWidth, rgba, padding, tint } from 'polished';
import pluralize from 'pluralize';
import { Link } from 'react-router-dom';

import playerSwapSelectedPlayerSelector from 'app/selectors/playerSwapSelectedPlayerSelector';

type Props = {
  errorMessages?: Array<string>,
  numFailed: number,
  numSucceeded: number,
  onReset: () => void
};

const defaultProps = {
  errorMessages: []
};

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column'
});

const Top = styled.div({
  display: 'flex',
  fontSize: '1.25rem',
  justifyContent: 'center',
  paddingBottom: '1rem',
  textAlign: 'center'
});

const Main = styled.div({
  alignItems: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto'
});

const Info = styled.div({
  alignItems: 'center',
  display: 'flex',
  paddingBottom: '0.5rem'
});

const SuccessIcon = styled.span(({ theme }) => ({
  color: theme.color.success,
  fontSize: '2rem',
  marginRight: '0.5em'
}));

const FailureIcon = styled.span(({ theme }) => ({
  color: theme.color.danger,
  fontSize: '2rem',
  marginRight: '0.5em'
}));

const ErrorMessage = styled.div({
  backgroundColor: rgba('red', 0.1),
  borderColor: 'red',
  borderStyle: 'solid',
  ...borderWidth(0, 0, 0, 5),
  ...padding('0.5rem', 0, '0.5rem', '0.75rem')
});

const ButtonSet = styled.div({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '1rem'
});

const ResetButton = styled('button')(({ theme }) => ({
  backgroundColor: theme.color.primary,
  borderRadius: 5,
  color: 'white',
  margin: '0 0.5em',
  padding: '0.375em 1em',
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: tint(0.75, theme.color.primary)
  }
}));

const ReturnLink = styled(Link)(({ theme }) => ({
  backgroundColor: theme.color.secondary,
  borderRadius: 5,
  color: 'white',
  margin: '0 0.25em',
  padding: '0.375em 1em',
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: tint(0.75, theme.color.basis4)
  }
}));

function PlayerSwapSuccess({ errorMessages, numFailed, numSucceeded, onReset }: Props) {
  return (
    <Wrapper>
      <Top>Submission processed.</Top>
      <Main>
        {numSucceeded > 0 && (
          <Info>
            <SuccessIcon className="ion-checkmark-circled" /> {numSucceeded}{' '}
            {pluralize('lineups', numSucceeded)} updated
          </Info>
        )}
        {numFailed > 0 && (
          <Info>
            <FailureIcon className="ion-close-circled" /> {numFailed}{' '}
            {pluralize('lineups', numFailed)} failed
          </Info>
        )}
      </Main>
      {errorMessages.length > 0 && (
        <ErrorMessage>
          {errorMessages.map(msg => (
            <div>{msg}</div>
          ))}
        </ErrorMessage>
      )}
      <ButtonSet>
        <ResetButton type="button" onClick={onReset}>
          Swap another
        </ResetButton>
        <ReturnLink to="/lobby">Back to Lobby</ReturnLink>
      </ButtonSet>
    </Wrapper>
  );
}

PlayerSwapSuccess.defaultProps = defaultProps;

const connector = connect(
  state => {
    const { softErrorMessages, submissionData } = state.playerSwap;
    const { lineups } = playerSwapSelectedPlayerSelector(state);
    return {
      errorMessages: softErrorMessages,
      numFailed: lineups.length - submissionData.length,
      numSucceeded: submissionData.length
    };
  },
  null
);

export default connector(PlayerSwapSuccess);
